import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
    Component,
    EventEmitter,
    input,
    Input,
    OnChanges,
    OnInit,
    output,
    Output,
    SimpleChanges,
} from '@angular/core';

import { MaterialDisabledActionDirective } from '../../../directives/material-disabled-action.directive';
import { MaterialAlertIconClass } from './material-alert.enum';

@Component({
    selector: 'ecm-mat-alert',
    templateUrl: 'material-alert.html',
    standalone: true,
    imports: [NgClass, NgTemplateOutlet, MaterialDisabledActionDirective],
})
export class MaterialAlertComponent implements OnInit, OnChanges {
    /**
     * Alert icon
     * @type {MaterialAlertIconClass}
     */
    public MaterialAlertIconClass = MaterialAlertIconClass;

    /**
     * Alert style
     * @type {danger | info | success | warning}
     */
    @Input() public style: 'danger' | 'info' | 'success' | 'warning' = 'danger';

    /**
     * Alert title
     * @type {string}
     */
    @Input() public title: string;

    /**
     * Alert paragraph to display
     * @type {string? | string[]?} text
     */
    @Input() public text?: string | string[];
    public displayedText: string[];

    /**
     * Alert confirmation style
     * @type {"button" | "link"}
     */
    @Input() public confirmationStyle?: 'button' | 'link' = 'button';

    /**
     * Alert confirmation label
     * @type {string}
     */
    @Input() public confirmationLabel?: string;

    /**
     * Alert confirmation path
     * @type {string}
     */
    @Input() public confirmationPath?: string;

    /**
     * Alert data-e2e tag
     * @type {string?}
     */
    @Input() public e2e?: string;

    /**
     * True to display the small version (smaller font and spaces)
     * @type {boolean}
     * @default false
     */
    @Input() public small = false;

    /**
     * True to display title in "55 Roman"
     * Only active with noLink/Button use case
     * @type {boolean}
     * @default false
     */
    @Input() public alertNormal = false;

    /**
     * True to display the button to the right of the title
     * @type {boolean}
     * @default false
     */
    @Input() public alertRow = false;

    /**
     * True to display Alert paragraph custom content to display
     * @type {boolean}
     * @default false
     */
    @Input() public contentTpl?: boolean = false;

    /**
     * Is disabled for eclair
     * @param {boolean} [isDisabledEclair=false]
     */
    @Input() public isDisabledEclair = false;

    /**
     * Input value for dismissible alert
     */
    public dismissibleAlert = input<string>();

    /**
     * Event emitter
     */
    @Output() public materialAlertEvent: EventEmitter<any> = new EventEmitter<any>();

    public dismissed = output<void>();

    /**
     * @inheritDoc
     */
    public ngOnInit(): void {
        this.e2e = (this.e2e || 'e2e-material-alert').concat('-', this.style);
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['text']) {
            if (typeof this.text === 'string') {
                this.text = [this.text];
            }
            this.displayedText = this.text;
        }
    }

    /**
     * Emit event on alert confirm
     */
    public materialAlertConfirm(): void {
        this.materialAlertEvent.emit();
    }

    /**
     * Is confirmation style link
     * @returns {boolean}
     */
    public isLinkConfirmationTemplate(): boolean {
        return this.confirmationStyle === 'link' || (this.confirmationStyle === 'button' && !!this.confirmationPath);
    }

    public dismiss(): void {
        this.dismissed.emit();
    }
}
